import { GetExtendChangeOrderResponse } from '../../api/protosCompiled/changeOrderCloseout/changeOrderCloseout_pb';
import {
  ExtendEstimate,
  GetExtendEstimateResponse,
} from '../../api/protosCompiled/estimateCloseout/estimateCloseout_pb';

import { useState } from 'react';

// Custom formatter function that handles NaN, undefined, and null values
const createSafeFormatter = (formatter: Intl.NumberFormat) => {
  return (value: number | undefined | null) => {
    // Check for invalid values
    if (value === undefined || value === null || isNaN(value)) {
      return formatter.format(0);
    }
    return formatter.format(value);
  };
};

/**
 * Formats a number to a currency string
 * Safely handles undefined, null, and NaN values
 */
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatCurrency = createSafeFormatter(currencyFormatter);

/**
 * Formats a number to a string with 3 decimal places
 * Safely handles undefined, null, and NaN values
 */
const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

export const formatNumber = createSafeFormatter(numberFormatter);

export const sxUnitCostOverrideValue = (item: ExtendEstimate.AsObject) => {
  if (item.isquoted) {
    return {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '5px',
    };
  }
  if (item.isoverrideitemunitcost) {
    return {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '5px',
    };
  }
  if (item.itemunitcost === 0) {
    return {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '5px',
    };
  } else {
    return {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '5px',
    };
  }
};

export const sxProductionCostOverrideValue = (item: ExtendEstimate.AsObject) => {
  if (item.isoverridehourlyproductionrate) {
    return {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '5px',
    };
  }
  if (item.hourlyproductionrate === 0) {
    return {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '5px',
    };
  } else {
    return {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '5px',
    };
  }
};

export const useCalculateTotalMaterialAndLaborCosts = () => {
  const [totalLaborHours, setTotalLaborHours] = useState<number>(0);
  const [totalMaterialCost, setTotalMaterialCost] = useState<number>(0);

  const calculateTotalMaterialAndLaborCosts = (
    extention: GetExtendEstimateResponse.AsObject | GetExtendChangeOrderResponse.AsObject
  ) => {
    if (extention) {
      //reduce the total labor hours
      const totalLaborHours = extention.extendestimatesList.reduce(
        (sum, data) => sum + data.totaldirectlaborhours,
        0
      );
      setTotalLaborHours(totalLaborHours);
      //reduce the total material cost
      const totalMaterialCost = extention.extendestimatesList.reduce(
        (sum, data) => sum + data.totalcost,
        0
      );

      setTotalMaterialCost(totalMaterialCost);
    }
    return { totalLaborHours, totalMaterialCost };
  };

  return calculateTotalMaterialAndLaborCosts;
};

/**
 * Perform safe division that handles undefined, null, and NaN values
 */
export const safeDivide = (
  numerator: number | undefined | null,
  denominator: number | undefined | null
) => {
  // Check for invalid values in numerator
  if (numerator === undefined || numerator === null || isNaN(numerator)) {
    return 0;
  }

  // Check for invalid values or zero in denominator
  if (
    denominator === undefined ||
    denominator === null ||
    denominator === 0 ||
    isNaN(denominator)
  ) {
    return 0;
  }

  const value = numerator / denominator;

  // Check if the result is valid
  if (isNaN(value) || !isFinite(value)) {
    return 0;
  }

  return value;
};

type AllocateCostProps = {
  costToAllocate: number;
  allocationPrecentage: number;
};
export const allocateCost = (props: AllocateCostProps) => {
  return safeDivide(props.costToAllocate * props.allocationPrecentage, 100);
};
